import MapboxDraw from '@mapbox/mapbox-gl-draw';
import distance from '@turf/distance';
import { point as turfPoint, circle as turfCircle } from '@turf/turf';
import createSupplementaryPointsForCircle from './utils/createSupplementaryPointsForCircle';

const DirectModeOverride = MapboxDraw.modes.direct_select;

DirectModeOverride.dragFeature = function (state, e, delta) {
    // Prevents the selected shape from being dragged when locked
    if (this.getSelected()[0].properties.isLocked === true) {
        return;
    }

    MapboxDraw.lib.moveFeatures(this.getSelected(), delta);

    this.getSelected()
        .filter(feature => feature.properties.radius)
        .map(circle => circle.properties.centre)
        .forEach(centre => {
            centre[0] += delta.lng;
            centre[1] += delta.lat;
        });

    state.dragMoveLocation = e.lngLat;
};

DirectModeOverride.dragVertex = function (state, e, delta) {
    if (this.getSelected()[0].properties.isLocked === true) {
        return;
    }

    if (state.feature.properties.radius) {
        const centre = state.feature.properties.centre;
        const movedVertex = [e.lngLat.lng, e.lngLat.lat];
        const radius = distance(turfPoint(centre), turfPoint(movedVertex), {
            units: 'meters'
        });
        const circleFeature = turfCircle(centre, radius, { units: 'meters' });

        state.feature.incomingCoords(circleFeature.geometry.coordinates);
        state.feature.properties.radius = radius;
        state.feature.properties.movedVertex = movedVertex;
        circleFeature.properties.radius = radius;
        circleFeature.properties.movedVertex = movedVertex;

        // below event causes latency, uncomment if needed desparately
        this.map.fire('draw.editing', {
            features: [circleFeature]
        });
    } else {
        const selectedCoords = state.selectedCoordPaths.map(coord_path =>
            state.feature.getCoordinate(coord_path)
        );
        const selectedCoordPoints = selectedCoords.map(coords => ({
            type: MapboxDraw.constants.geojsonTypes.FEATURE,
            properties: {},
            geometry: {
                type: MapboxDraw.constants.geojsonTypes.POINT,
                coordinates: coords
            }
        }));

        const constrainedDelta = MapboxDraw.lib.constrainFeatureMovement(
            selectedCoordPoints,
            delta
        );

        for (let i = 0; i < selectedCoords.length; i++) {
            const coord = selectedCoords[i];
            state.feature.updateCoordinate(
                state.selectedCoordPaths[i],
                coord[0] + constrainedDelta.lng,
                coord[1] + constrainedDelta.lat
            );
        }
    }
};

DirectModeOverride.onMouseUp = function (state, props) {
    if (state.feature.properties.isCircle) {
        state.feature.properties.movedVertex = undefined;
    }

    this.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT, {
        featureIds: [state?.featureId]
    });
};

DirectModeOverride.toDisplayFeatures = function (state, geojson, push) {
    if (state.featureId === geojson.properties.id) {
        geojson.properties.active = MapboxDraw.constants.activeStates.ACTIVE;

        push(geojson);

        const supplementaryPoints = geojson.properties.user_isCircle
            ? createSupplementaryPointsForCircle(geojson)
            : MapboxDraw.lib.createSupplementaryPoints(geojson, {
                  map: this.map,
                  midpoints: true,
                  selectedPaths: state.selectedCoordPaths
              });
        supplementaryPoints.forEach(push);
        const selectedPoints = this?._ctx?.api?.getSelectedPoints()?.features;

        if (selectedPoints?.length > 0 && geojson?.properties?.user_movedVertex) {
            const centre = state.feature.properties.centre;

            push({
                type: MapboxDraw.constants.geojsonTypes.FEATURE,
                geometry: {
                    type: MapboxDraw.constants.geojsonTypes.LINE_STRING,
                    coordinates: [centre, geojson?.properties?.user_movedVertex]
                },
                properties: {
                    parent: geojson.properties.id,
                    active: 'true'
                }
            });

            const circleFeature = turfCircle(
                centre,
                distance(turfPoint(centre), turfPoint(geojson?.properties?.user_movedVertex), {
                    units: 'meters'
                }),
                { units: 'meters' }
            );

            this.map.fire('draw.editing', {
                features: [circleFeature]
            });
        }
    } else {
        geojson.properties.active = MapboxDraw.constants.activeStates.INACTIVE;
        push(geojson);
    }
    this.fireActionable(state);
};

export default DirectModeOverride;
